"use client";

import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { LanguageToggle } from "@/components/LanguageToggle";
import { LOCALE_COOKIE, type Locale, t } from "@/lib/i18n";
import { useEffect, useMemo, useRef, useState } from "react";
import type { PermissionSet } from "@/lib/authTypes";

type NotificationItem = {
  id: number;
  title: string;
  body: string | null;
  href: string | null;
  isRead: boolean;
  createdAt: string;
};

type NavLink = {
  href: string;
  labelKey:
    | "nav.dashboard"
    | "nav.tickets"
    | "nav.newTicket"
    | "nav.users"
    | "nav.catalog"
    | "nav.routine"
    | "nav.assets"
    | "nav.admin";
  active: boolean;
};

function getLocaleFromCookie(): Locale {
  if (typeof document === "undefined") return "en";
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${LOCALE_COOKIE}=([^;]*)`),
  );
  const value = match ? decodeURIComponent(match[1]) : "";
  return value === "ar" ? "ar" : "en";
}

function MenuIcon({ open }: { open: boolean }) {
  return (
    <span className="relative block h-4 w-5" aria-hidden="true">
      <span
        className={`absolute left-0 top-0 block h-0.5 w-5 rounded-full bg-current transition ${
          open ? "translate-y-1.5 rotate-45" : ""
        }`}
      />
      <span
        className={`absolute left-0 top-1.5 block h-0.5 w-5 rounded-full bg-current transition ${
          open ? "opacity-0" : ""
        }`}
      />
      <span
        className={`absolute left-0 top-3 block h-0.5 w-5 rounded-full bg-current transition ${
          open ? "-translate-y-1.5 -rotate-45" : ""
        }`}
      />
    </span>
  );
}

export function TopNav({
  employeeId,
  isAdmin,
  permissions,
}: {
  employeeId: string;
  isAdmin: boolean;
  permissions: PermissionSet["pages"];
}) {
  const pathname = usePathname() || "/";
  const [locale, setLocale] = useState<Locale>("en");
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const notificationsRef = useRef<HTMLDivElement | null>(null);
  const accountMenuRef = useRef<HTMLDivElement | null>(null);
  const mobileMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setLocale(getLocaleFromCookie());
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setNotificationsOpen(false);
    setAccountMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    async function loadNotifications() {
      try {
        const res = await fetch("/api/notifications", { cache: "no-store" });
        const data = await res.json().catch(() => null);
        if (!res.ok || !data) return;
        setNotifications(data.items ?? []);
        setUnreadCount(Number(data.unreadCount ?? 0));
      } catch {
        // ignore nav polling errors
      }
    }

    loadNotifications();
    const timer = window.setInterval(loadNotifications, 30000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    function handleOutsideClick(event: MouseEvent) {
      const target = event.target as Node;
      if (
        notificationsRef.current &&
        !notificationsRef.current.contains(target)
      ) {
        setNotificationsOpen(false);
      }
      if (accountMenuRef.current && !accountMenuRef.current.contains(target)) {
        setAccountMenuOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(target)) {
        setMobileMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setMobileMenuOpen(false);
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [mobileMenuOpen]);

  const rtl = locale === "ar";
  const navItemClass =
    "whitespace-nowrap rounded-full px-3 py-1.5 font-medium transition";
  const navItemIdleClass =
    "text-slate-600 hover:bg-slate-100 hover:text-slate-900";
  const navItemActiveClass = "bg-sky-100 text-sky-700";

  const isDashboard = pathname === "/";
  const isTickets =
    (pathname === "/tickets" || pathname.startsWith("/tickets/")) &&
    !pathname.endsWith("/new");
  const isNewTicket = pathname === "/tickets/new";
  const isUsers = pathname === "/users" || pathname.startsWith("/users/");
  const isCatalog = pathname === "/catalog" || pathname.startsWith("/catalog/");
  const isRoutine =
    pathname === "/routine" || pathname.startsWith("/routine/");
  const isAssets = pathname === "/assets" || pathname.startsWith("/assets/");
  const isAdminAccounts =
    pathname === "/admin/accounts" || pathname.startsWith("/admin/accounts/");

  const navLinks = useMemo(() => {
    const links: NavLink[] = [];
    if (permissions.dashboard) {
      links.push({ href: "/", labelKey: "nav.dashboard", active: isDashboard });
    }
    if (permissions.tickets) {
      links.push({
        href: "/tickets",
        labelKey: "nav.tickets",
        active: isTickets,
      });
    }
    if (permissions.newTicket) {
      links.push({
        href: "/tickets/new",
        labelKey: "nav.newTicket",
        active: isNewTicket,
      });
    }
    if (permissions.users) {
      links.push({ href: "/users", labelKey: "nav.users", active: isUsers });
    }
    if (permissions.catalog) {
      links.push({
        href: "/catalog",
        labelKey: "nav.catalog",
        active: isCatalog,
      });
    }
    if (permissions.routineDashboard || permissions.inspections) {
      links.push({
        href: "/routine/inspections",
        labelKey: "nav.routine",
        active: isRoutine,
      });
    }
    if (permissions.assets || permissions.assetCategories) {
      links.push({
        href: permissions.assets ? "/assets" : "/assets/settings",
        labelKey: "nav.assets",
        active: isAssets,
      });
    }
    if (isAdmin && permissions.adminAccounts) {
      links.push({
        href: "/admin/accounts",
        labelKey: "nav.admin",
        active: isAdminAccounts,
      });
    }
    return links;
  }, [
    permissions,
    isAdmin,
    isDashboard,
    isTickets,
    isNewTicket,
    isUsers,
    isCatalog,
    isRoutine,
    isAssets,
    isAdminAccounts,
  ]);

  async function logout() {
    await fetch("/api/auth/logout", { method: "POST" }).catch(() => {});
    window.location.href = "/login";
  }

  async function markAllRead() {
    await fetch("/api/notifications", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ all: true }),
    }).catch(() => {});
    setNotifications((items) => items.map((item) => ({ ...item, isRead: true })));
    setUnreadCount(0);
  }

  async function openNotification(item: NotificationItem) {
    if (!item.isRead) {
      await fetch("/api/notifications", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: item.id }),
      }).catch(() => {});
      setNotifications((items) =>
        items.map((entry) =>
          entry.id === item.id ? { ...entry, isRead: true } : entry,
        ),
      );
      setUnreadCount((count) => Math.max(0, count - 1));
    }

    if (item.href) {
      window.location.href = item.href;
    }
  }

  return (
    <div className="border-b border-slate-200 bg-white/80 backdrop-blur" ref={mobileMenuRef}>
      <div className="mx-auto flex w-full items-center justify-between gap-2 px-4 py-2.5 sm:px-6">
        <Link href="/" className="flex min-w-0 shrink items-center gap-2.5 sm:gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white shadow-sm ring-1 ring-slate-200">
            <Image
              src="/brand-logo.png"
              alt="Al-Amer logo"
              width={32}
              height={32}
              className="h-8 w-8 object-cover"
            />
          </div>
          <div
            className={`min-w-0 leading-tight ${rtl ? "text-right" : "text-left"}`}
          >
            <div className="truncate text-sm font-semibold text-slate-900">
              Abdulla Ali Alamer &amp; Bros Co.
            </div>
            <div className="hidden truncate text-[11px] text-slate-500 sm:block">
              Web &amp; Telegram Ticketing System
            </div>
          </div>
        </Link>

        <nav className="mx-4 hidden flex-1 flex-wrap items-center justify-start gap-1.5 text-xs lg:flex md:text-sm">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className={`${navItemClass} ${
                link.active ? navItemActiveClass : navItemIdleClass
              }`}
            >
              {t(locale, link.labelKey)}
            </Link>
          ))}
        </nav>

        <div className="flex shrink-0 items-center gap-1">
          <div className="relative" ref={notificationsRef}>
            <button
              type="button"
              onClick={() => {
                setNotificationsOpen((open) => !open);
                setAccountMenuOpen(false);
                setMobileMenuOpen(false);
              }}
              className={`${navItemClass} relative ${navItemIdleClass}`}
              title="Notifications"
            >
              <span aria-hidden="true">🔔</span>
              {unreadCount > 0 && (
                <span className="absolute -right-1 -top-1 min-w-[18px] rounded-full bg-red-500 px-1.5 text-[10px] font-semibold text-white">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </button>
            {notificationsOpen && (
              <div
                className={`z-30 mt-2 w-[min(20rem,calc(100vw-2rem))] rounded-xl border border-slate-200 bg-white p-2 shadow-lg ${
                  rtl ? "absolute left-0" : "absolute right-0"
                }`}
              >
                <div className="mb-2 flex items-center justify-between px-2 py-1">
                  <span className="text-sm font-semibold text-slate-800">
                    Notifications
                  </span>
                  {unreadCount > 0 && (
                    <button
                      type="button"
                      onClick={markAllRead}
                      className="text-xs text-sky-700 hover:text-sky-900"
                    >
                      Mark all read
                    </button>
                  )}
                </div>
                {notifications.length === 0 ? (
                  <div className="px-2 py-3 text-sm text-slate-500">
                    No notifications yet.
                  </div>
                ) : (
                  <div className="max-h-96 space-y-1 overflow-y-auto">
                    {notifications.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => openNotification(item)}
                        className={`w-full rounded-lg px-3 py-2 hover:bg-slate-50 ${
                          rtl ? "text-right" : "text-left"
                        } ${item.isRead ? "bg-white" : "bg-sky-50"}`}
                      >
                        <div className="text-sm font-medium text-slate-800">
                          {item.title}
                        </div>
                        {item.body && (
                          <div className="mt-0.5 text-xs text-slate-600">
                            {item.body}
                          </div>
                        )}
                        <div className="mt-1 text-[11px] text-slate-400">
                          {new Date(item.createdAt).toLocaleString()}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <LanguageToggle locale={locale} />

          <div className="relative" ref={accountMenuRef}>
            <button
              type="button"
              onClick={() => {
                setAccountMenuOpen((open) => !open);
                setNotificationsOpen(false);
                setMobileMenuOpen(false);
              }}
              className={`${navItemClass} inline-flex items-center gap-1.5 ${
                accountMenuOpen ? navItemActiveClass : navItemIdleClass
              }`}
              aria-expanded={accountMenuOpen}
              aria-haspopup="menu"
            >
              <span className="font-medium tabular-nums">{employeeId}</span>
              <svg
                aria-hidden="true"
                viewBox="0 0 20 20"
                fill="currentColor"
                className={`h-3.5 w-3.5 transition ${
                  accountMenuOpen ? "rotate-180" : ""
                }`}
              >
                <path
                  fillRule="evenodd"
                  d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
            {accountMenuOpen && (
              <div
                role="menu"
                className={`z-30 mt-2 min-w-[10rem] rounded-xl border border-slate-200 bg-white p-1 shadow-lg ${
                  rtl ? "absolute left-0" : "absolute right-0"
                }`}
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={logout}
                  className={`w-full rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 ${
                    rtl ? "text-right" : "text-left"
                  }`}
                >
                  {t(locale, "nav.logout")}
                </button>
              </div>
            )}
          </div>

          <button
            type="button"
            className={`inline-flex h-10 w-10 items-center justify-center rounded-lg text-slate-700 hover:bg-slate-100 lg:hidden ${
              mobileMenuOpen ? "bg-slate-100" : ""
            }`}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-nav-menu"
            aria-label={
              mobileMenuOpen
                ? t(locale, "nav.closeMenu")
                : t(locale, "nav.openMenu")
            }
            onClick={() => {
              setMobileMenuOpen((open) => !open);
              setNotificationsOpen(false);
              setAccountMenuOpen(false);
            }}
          >
            <MenuIcon open={mobileMenuOpen} />
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div
          id="mobile-nav-menu"
          className="border-t border-slate-200 bg-white px-4 py-3 lg:hidden"
        >
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
            {t(locale, "nav.menu")}
          </p>
          <nav className="flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className={`rounded-lg px-3 py-2.5 text-sm font-medium transition ${
                  link.active
                    ? "bg-sky-100 text-sky-700"
                    : "text-slate-700 hover:bg-slate-50"
                }`}
              >
                {t(locale, link.labelKey)}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </div>
  );
}
